'use client'

import { QUERY_REFETCH_INTERVALS, QUERY_STALE_TIMES } from '@/constants/query'
import { getWalletProvider } from '@/lib/wallets/registry'
import type { BridgePlatform, TokenBalance, WalletRuntime } from '@/lib/wallets/types'
import { useWallet } from '@/providers/WalletContext'
import { useQuery } from '@tanstack/react-query'

export function useMarketTokenBalance({
	platform,
	tokenAddress,
	decimals,
	symbol = 'Token',
	address,
}: {
	platform: BridgePlatform
	tokenAddress: string | undefined
	decimals: number
	symbol?: string
	address?: string
}) {
	const wallet = useWallet()
	const provider = getWalletProvider(platform)
	const runtime: WalletRuntime = {
		eoaAddress: wallet.eoaAddress,
		solanaAddress: wallet.solanaAddress,
		publicClient: wallet.publicClient,
		basePublicClient: wallet.basePublicClient,
		solanaConnection: wallet.solanaConnection,
		polymarketSafe: wallet.polymarketSafe,
		polymarketActiveWallet: wallet.polymarketActiveWallet,
	}
	const owner = address ?? provider.resolveAddress(runtime)

	return useQuery<TokenBalance | null>({
		queryKey: ['market-token-balance', platform, owner, tokenAddress, decimals],
		queryFn: () => {
			if (!(provider.readTokenBalance && tokenAddress)) return null
			return provider.readTokenBalance(runtime, { address: owner, tokenAddress, decimals, symbol })
		},
		enabled: !!(owner && tokenAddress && provider.readTokenBalance),
		staleTime: QUERY_STALE_TIMES.BALANCE,
		refetchInterval: QUERY_REFETCH_INTERVALS.BALANCE,
	})
}
